import { Divider, LinearProgress } from '@mui/material'

import { FormFieldDto } from '../../interface/enrollmentApplication/EnrollementQuestionnaireSchema'
import React from 'react'
import SectionRow from '../common/SectionRow'
import { TextField } from './FormFields'
import styled from '@emotion/styled'
import theme from '../../global/theme'

interface DisclosureStepComponentProps {
  mothersMaidenName: string
  onMothersMaidenNameChange: (value: string) => void
  error?: string
}

const DisclosureParagraph = styled.p`
  color: ${theme.colors.textGrey};
  margin-bottom: 20px;
  font-size: 12px;
`

export const DisclosureStepComponent: React.FC<
  DisclosureStepComponentProps
> = ({ mothersMaidenName, onMothersMaidenNameChange, error }) => {
  return (
    <SectionRow>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '10px 0px',
        }}
      >
        <LinearProgress
          style={{ flex: '1' }}
          variant='determinate'
          value={100}
        />
        <span
          style={{
            color: theme.colors.textGrey,
            fontSize: '14px',
            minWidth: '80px',
          }}
        >
          Final Step
        </span>
      </div>

      <h2 style={{ fontSize: '24px', color: theme.colors.primary }}>
        Disclosure
      </h2>

      <p
        style={{
          color: theme.colors.textGrey,
          marginBottom: '20px',
        }}
      >
        Please review the disclosure information and answer the following
        question:
        <Divider style={{ paddingTop: '10px' }} />
      </p>

      {/* Mother's Maiden Name Field */}
      <TextField
        field={
          {
            id: 'mothers_maiden_name',
            display_label: "Mother's Maiden Name",
            placeholder: "Enter mother's maiden name",
            type: 'text_field',
            maxlength: 100,
          } as FormFieldDto
        }
        value={mothersMaidenName}
        onChange={onMothersMaidenNameChange}
        error={error}
        isRequired={true}
      />

      {/* Disclosure Text */}
      <div
        style={{
          backgroundColor: theme.colors.backgroundColorContainer,
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <h3
          style={{
            marginTop: 0,
            color: theme.colors.primary,
            fontSize: '14px',
            marginBottom: '10px',
          }}
        >
          CONSENT TO USE OF ELECTRONIC SIGNATURES AND RECEIPT OF DISCLOSURES
          EXCLUSIVELY THROUGH ELECTRONIC MEANS
        </h3>

        <DisclosureParagraph>
          Thank you for using Elite Insurance Partners LLC's electronic
          application process. You are applying for insurance coverage using
          electronic records, transactions and signatures. Elite Insurance
          Partners, LLC is legally required to provide you with certain
          disclosures and information about your insurance application
          ("Required Information"). If you give consent, we can deliver the
          Required Information to you electronically. Your consent also permits
          the general use of electronic records and electronic signatures in
          connection with your application.
        </DisclosureParagraph>

        <DisclosureParagraph style={{ fontWeight: 'bold' }}>
          This is important information. PLEASE READ THIS NOTICE CAREFULLY AND
          PRINT OR DOWNLOAD A COPY FOR YOUR FILES.
        </DisclosureParagraph>

        <h4
          style={{
            color: theme.colors.primary,
            fontSize: '14px',
            marginBottom: '10px',
          }}
        >
          Consent to Use of Electronic Signatures and Disclosures
        </h4>

        <DisclosureParagraph>
          By electronically signing this document, you agree to the use of
          electronic transactions and electronic signatures and to the receipt
          of electronic versions of records. You also agree to be held to any
          agreement you make or transmit through the Internet or this Web site,
          including your consent to receive the Required Information from us
          only by electronic transmission. You agree that, by using this Web
          site, your consent will be as legally binding and enforceable as if
          you had signed on paper.
        </DisclosureParagraph>

        <DisclosureParagraph>
          If you have elected to allow for Required Information to be sent to an
          email address or cellular phone capable of receiving text messages
          that you have provided to Elite Insurance Partners, LLC, then you
          should be aware that Elite Insurance Partners, LLC rightfully
          considers this election to be consent by you that all notices may be
          sent electronically. Therefore, you should be diligent in updating the
          electronic contact information provided to Elite Insurance Partners,
          LLC on behalf of insurance company and its affiliates in the event
          that the electronic contact information should change.
        </DisclosureParagraph>

        <DisclosureParagraph>
          If Elite Insurance Partners, LLC notifies you electronically that
          information is available to review on a secure Web site, then you
          agree that delivery of that information is deemed to have been
          completed upon your receipt of Elite Insurance Partners, LLC's
          notification.
        </DisclosureParagraph>

        <DisclosureParagraph>
          You are entitled to change your mind and withdraw your consent.
          However, you would then no longer be able to use this section of the
          Web site or receive electronic versions of Required Information and
          other records. If you also want a paper copy of the Required
          Information, but still want Elite Insurance Partners, LLC to process
          your application electronically, we will still need your consent to
          receive Required Information electronically. You will not be charged
          for paper copies of any information you request.
        </DisclosureParagraph>

        <DisclosureParagraph>
          If you wish to (1) change the electronic contact information you have
          provided to Elite Insurance Partners, LLC on behalf of the insurance
          company and its affiliates; (2) withdraw consent to receive electronic
          delivery of future Required Information and other records; or (3)
          request a paper copy of the Required Information after your
          application has already been submitted, please contact your agent at
          the address or telephone number given to you when you applied for
          coverage or Elite Insurance Partners, LLC at (800) 845-2484.
        </DisclosureParagraph>

        <DisclosureParagraph>
          If you consent to electronic delivery of Required Information and then
          withdraw that consent, Elite Insurance Partners, LLC cannot continue
          processing your application electronically. You can still apply on our
          paper application, but it could take longer to process your
          application.
        </DisclosureParagraph>

        <DisclosureParagraph>
          Your consent applies to all Required Information that Elite Insurance
          Partners, LLC gives you or receives from you about your insurance
          application and the notices, disclosures, and other documents related
          to it.
        </DisclosureParagraph>

        <DisclosureParagraph>
          You agree to print out or download Required Information for your
          records when we advise you to. If you have any trouble printing out or
          downloading any Required Information, please contact your agent at the
          address or telephone number given to you when you applied for coverage
          or Elite Insurance Partners, LLC at (800) 845-2484.
        </DisclosureParagraph>

        <DisclosureParagraph>
          The computer hardware and software used to access this page on the
          Internet, along with an email address or cell phone number, is all you
          will need to access the application for insurance, related notices,
          disclosures, authorizations, acknowledgements and other documents
          provided to you in electronic form. To retain copies of these
          documents, you may (1) print them from this Web site, (2) email them
          to an address where you can print them on paper, or (3) save an
          electronic copy onto a computer with at least 100 megabytes of memory.
          If you do not have the required software and/or hardware, or if you do
          not wish to use electronic records and signatures for any other
          reason, you can ask Elite Insurance Partners, LLC to send you paper
          copies of the application document(s) instead. Elite Insurance
          Partners, LLC may require that certain communications from you be
          delivered to them on paper at a specified address.
        </DisclosureParagraph>

        <DisclosureParagraph style={{ fontWeight: 'bold' }}>
          By clicking "Submit", I confirm to you that:
        </DisclosureParagraph>

        <ul
          style={{
            color: theme.colors.textGrey,
            fontSize: '12px',
            marginBottom: '20px',
            paddingLeft: '20px',
          }}
        >
          <li style={{ marginBottom: '8px' }}>
            I can access and read this "Consent to Use of Electronic Signatures
            and Receipt of Disclosures Exclusively Through Electronic Means;"
            and
          </li>
          <li style={{ marginBottom: '8px' }}>
            I have an account with an Internet service provider, and I am able
            to send email or text messages and receive email or text messages
            with hyperlinks to Web sites and/or attached files; and
          </li>
          <li style={{ marginBottom: '8px' }}>
            Until or unless I notify Elite Insurance Partners, LLC as described
            above, I consent to receive all required notices and disclosures
            relating to my application for insurance exclusively through
            electronic means; and
          </li>
          <li style={{ marginBottom: '8px' }}>
            I also consent to the use of electronic signatures in connection
            with my insurance application in place of handwritten signatures;
            and
          </li>
          <li style={{ marginBottom: '8px' }}>I am authorized to consent.</li>
        </ul>
      </div>
    </SectionRow>
  )
}

export default DisclosureStepComponent
